import React from 'react';
import { Row, Col, Button, Badge } from 'react-bootstrap';

type OmiseConfig = {
  sourceTypes: Array<[string, string]>;
};

type OmiseDrawerProps = {
  showSelector: boolean;
  showQr: boolean;
  omiseConfig: OmiseConfig | null;
  qrCodeData?: string | null;
  status: 'pending' | 'successful' | 'failed' | 'expired';
  statusMessage: string;
  onSelectSource: (sourceType: string) => void;
  onClose: () => void;
};

const getOmiseGroups = (sourceTypes: Array<[string, string]>) => {
  const groups: Record<string, Array<[string, string]>> = {
    credit_card: [],
    promptpay: [],
    wallets: [],
    mobile_banking: [],
    internet_banking: [],
    other: [],
  };
  sourceTypes.forEach(([code, name]) => {
    if (code === 'credit_card') groups.credit_card.push([code, name]);
    else if (code === 'promptpay') groups.promptpay.push([code, name]);
    else if (code === 'truemoney' || code === 'shopeepay' || code === 'grabpay') groups.wallets.push([code, name]);
    else if (code.startsWith('mobile_banking')) groups.mobile_banking.push([code, name]);
    else if (code.startsWith('internet_banking')) groups.internet_banking.push([code, name]);
    else groups.other.push([code, name]);
  });
  return groups;
};

const OmiseDrawer: React.FC<OmiseDrawerProps> = ({
  showSelector,
  showQr,
  omiseConfig,
  qrCodeData,
  status,
  statusMessage,
  onSelectSource,
  onClose,
}) => {
  if (!showSelector && !showQr) return null;

  return (
    <>
      <div className="omise-drawer-overlay" onClick={onClose} />
      <aside
        className="omise-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="omise-drawer-title"
      >
        <div className="omise-drawer-header">
          <h5 id="omise-drawer-title" className="mb-0">
            {showSelector ? 'Select Omise Payment Method' : 'PromptPay QR Payment'}
          </h5>
          <button
            type="button"
            className="omise-drawer-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="omise-drawer-body">
          {showSelector && (
            <>
              {omiseConfig ? (
                (() => {
                  const groups = getOmiseGroups(omiseConfig.sourceTypes);
                  return (
                    <>
                      {groups.credit_card.length > 0 && (
                        <>
                          <h6 className="mb-2">Credit / Debit Card</h6>
                          <div className="d-grid gap-2 mb-3">
                            {groups.credit_card.map(([code, name]) => (
                              <Button
                                key={code}
                                variant="outline-primary"
                                onClick={() => onSelectSource(code)}
                              >
                                {name}
                              </Button>
                            ))}
                          </div>
                        </>
                      )}

                      {groups.promptpay.length > 0 && (
                        <>
                          <h6 className="mb-2">QR Payments</h6>
                          <div className="d-grid gap-2 mb-3">
                            {groups.promptpay.map(([code, name]) => (
                              <Button
                                key={code}
                                variant="outline-success"
                                onClick={() => onSelectSource(code)}
                              >
                                {name}
                              </Button>
                            ))}
                          </div>
                        </>
                      )}

                      {groups.wallets.length > 0 && (
                        <>
                          <h6 className="mb-2">E-Wallets</h6>
                          <div className="d-grid gap-2 mb-3">
                            {groups.wallets.map(([code, name]) => (
                              <Button
                                key={code}
                                variant="outline-secondary"
                                onClick={() => onSelectSource(code)}
                              >
                                {name}
                              </Button>
                            ))}
                          </div>
                        </>
                      )}

                      {groups.mobile_banking.length > 0 && (
                        <>
                          <h6 className="mb-2">Mobile Banking</h6>
                          <div className="d-grid gap-2 mb-3">
                            {groups.mobile_banking.map(([code, name]) => (
                              <Button
                                key={code}
                                variant="outline-secondary"
                                onClick={() => onSelectSource(code)}
                              >
                                {name}
                              </Button>
                            ))}
                          </div>
                        </>
                      )}

                      {groups.internet_banking.length > 0 && (
                        <>
                          <h6 className="mb-2">Internet Banking</h6>
                          <div className="d-grid gap-2 mb-3">
                            {groups.internet_banking.map(([code, name]) => (
                              <Button
                                key={code}
                                variant="outline-secondary"
                                onClick={() => onSelectSource(code)}
                              >
                                {name}
                              </Button>
                            ))}
                          </div>
                        </>
                      )}

                      {groups.other.length > 0 && (
                        <>
                          <h6 className="mb-2">Other</h6>
                          <div className="d-grid gap-2">
                            {groups.other.map(([code, name]) => (
                              <Button
                                key={code}
                                variant="outline-secondary"
                                onClick={() => onSelectSource(code)}
                              >
                                {name}
                              </Button>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()
              ) : (
                <p className="mb-0 text-muted">Loading payment methods...</p>
              )}
            </>
          )}

          {showQr && (
            <Row className="align-items-center">
              <Col md={6} className="text-center mb-3 mb-md-0">
                {qrCodeData && (
                  <div className="p-3 border rounded bg-white">
                    <img
                      src={qrCodeData}
                      alt="PromptPay QR Code"
                      style={{ maxWidth: 260, width: '100%', height: 'auto' }}
                    />
                  </div>
                )}
                {qrCodeData && (
                  <div className="mt-3">
                    <a href={qrCodeData} download="promptpay_qr.png" className="btn btn-outline-primary">
                      Download QR Code
                    </a>
                  </div>
                )}
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <span className="text-muted d-block mb-1">Status</span>
                  <Badge bg={status === 'pending' ? 'info' : status === 'successful' ? 'success' : 'danger'}>
                    {status}
                  </Badge>
                </div>
                <p className="text-muted mb-3">{statusMessage}</p>
                <ul className="text-muted ps-3 mb-0">
                  <li>Open your banking app and scan the QR code</li>
                  <li>Confirm the amount and approve the payment</li>
                  <li>This window will update automatically</li>
                </ul>
              </Col>
            </Row>
          )}
        </div>

        <div className="omise-drawer-footer">
          <Button variant="outline-secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </aside>
    </>
  );
};

export default OmiseDrawer;
